import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, setAdminToken, API } from "../lib/api";
import RawHeader from "../components/RawHeader";
import ClothingViewer3D from "../components/ClothingViewer3D";
import ViewerErrorBoundary from "../components/ViewerErrorBoundary";
import DimensionTable from "../components/DimensionTable";
import HelpDiagram from "../components/HelpDiagram";

const EMPTY_DIMS = {
  S: { chest: 0, length: 0, sleeve: 0, shoulder: 0, waist: 0, hem: 0 },
  M: { chest: 0, length: 0, sleeve: 0, shoulder: 0, waist: 0, hem: 0 },
  L: { chest: 0, length: 0, sleeve: 0, shoulder: 0, waist: 0, hem: 0 },
  XL: { chest: 0, length: 0, sleeve: 0, shoulder: 0, waist: 0, hem: 0 },
};

const CLOTHING_TYPES = [
  "T-Shirt", "Hoodie", "Sweatshirt", "Jumper", "Jacket", "Shirt",
  "Pants", "Shorts", "Jeans", "Cap", "Dress",
];

const AdminDesignStudio = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    name: "RAW COMPANY ",
    clothing_type: "Hoodie",
    garment_style: "",
    description: "",
    material: "100% Cotton French Terry, 380 GSM",
    color: "#111111",
    price: 85.0,
    currency: "gbp",
    stock: 50,
    image_base64: "",
    image_back: "",
    image_side: "",
    dimensions: EMPTY_DIMS,
    materials: [],
    logo_overlay: { logo_id: "", image_base64: "", placement: "none", size: 1 },
    factory_notes: "",
    tags: [],
    gender: "unisex",
    units_per_size: 1,
    cost_to_make: 0,
    reference_image: "",  // optional designer reference for factory notes
    published: true,
    drop_number: 1,  // collection grouping — Drop 01, Drop 02, ...
    gallery_images: [],  // extra detail shots shown on product page
  });

  const [genPrompt, setGenPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [suggestingDims, setSuggestingDims] = useState(false);
  const [genNotes, setGenNotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingPreset, setLoadingPreset] = useState(false);
  const [materialLibrary, setMaterialLibrary] = useState([]);
  const [garmentStyles, setGarmentStyles] = useState([]);
  const [savedLogos, setSavedLogos] = useState([]);
  const [viewMode, setViewMode] = useState("3d"); // img | 3d — default to 3D so they see wireframe
  const [helpOpen, setHelpOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteMood, setPaletteMood] = useState("");
  const [palettes, setPalettes] = useState([]);
  const [palLoading, setPalLoading] = useState(false);

  // Load material library once
  useEffect(() => {
    api.get("/presets/materials")
      .then((r) => setMaterialLibrary(r.data.materials || []))
      .catch(() => {});
    api.get("/logos")
      .then((r) => setSavedLogos(r.data || []))
      .catch(() => {});
  }, []);

  // Load garment styles when clothing type changes
  useEffect(() => {
    if (!form.clothing_type) return;
    api.get(`/presets/garment-styles/${encodeURIComponent(form.clothing_type)}`)
      .then((r) => setGarmentStyles(r.data.styles || []))
      .catch(() => setGarmentStyles([]));
  }, [form.clothing_type]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/products/${id}`)
      .then((r) => {
        setForm((f) => ({ ...f, ...r.data, gender: r.data.gender || "unisex" }));
        // Restore the AI prompt text so editing resumes with the exact brief
        if (r.data.ai_prompt) setGenPrompt(r.data.ai_prompt);
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          setAdminToken(null);
          nav("/admin/login");
        } else toast.error("Failed to load design");
      });
  }, [id, isEdit, nav]);

  // ---------- AUTO-SAVE (only for NEW designs, to localStorage) ----------
  const DRAFT_KEY = "raw_design_draft";

  // On first mount for NEW design, offer to restore previous unsaved draft
  const [restorePromptShown, setRestorePromptShown] = useState(false);
  useEffect(() => {
    if (isEdit) return;
    if (restorePromptShown) return;
    const saved = localStorage.getItem(DRAFT_KEY);
    if (!saved) return;
    try {
      const { form: savedForm, ts } = JSON.parse(saved);
      if (savedForm && savedForm.name) {
        const age = Math.round((Date.now() - ts) / 60000);
        const ok = window.confirm(
          `Unsaved draft found from ${age} min ago:\n"${savedForm.name}"\n\nRestore it?`
        );
        if (ok) setForm(savedForm);
        else localStorage.removeItem(DRAFT_KEY);
      }
      setRestorePromptShown(true);
    } catch {
      localStorage.removeItem(DRAFT_KEY);
    }
    // eslint-disable-next-line
  }, [isEdit]);

  // Debounced auto-save to localStorage on every form change (new designs only)
  useEffect(() => {
    if (isEdit) return;
    // Don't save empty/initial state
    if (!form.name && !form.description && !form.image_base64) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, ts: Date.now() }));
      } catch {
        // Quota exceeded (images too big) - save without image
        try {
          const slim = { ...form, image_base64: "" };
          localStorage.setItem(DRAFT_KEY, JSON.stringify({ form: slim, ts: Date.now() }));
        } catch {}
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [form, isEdit]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Shared helper: run the auto-complete step (dimensions + fabrics + factory notes).
  // `forceOverwrite` = rebuild notes/materials/dims even if the form already has values.
  const runAutoComplete = async (prompt, { forceOverwrite = false } = {}) => {
    try {
      const auto = await api.post("/ai/auto-complete-design", {
        clothing_type: form.clothing_type,
        garment_style: form.garment_style,
        color: form.color,
        description: prompt,
        material: form.material,
        units_per_size: parseInt(form.units_per_size) || 1,
        logo_placement: form.logo_overlay?.placement || "",
        // Auto-attach the generated product image as the factory reference so the
        // factory sees exactly what was designed — no manual upload needed.
        reference_image_url: form.reference_image || form.image_base64 || "",
      });
      const dimsEmpty = !form.dimensions?.M?.chest;
      const matsEmpty = !form.materials || form.materials.length === 0;
      const notesEmpty = !form.factory_notes?.trim();
      const materialIsDefault = !form.material || form.material.trim().startsWith("100% Cotton French Terry");
      const hint = auto.data.main_material_hint;
      setForm((f) => ({
        ...f,
        dimensions: (forceOverwrite || dimsEmpty) ? (auto.data.dimensions || f.dimensions) : f.dimensions,
        materials:  (forceOverwrite || matsEmpty) ? (auto.data.materials || []) : f.materials,
        factory_notes: (forceOverwrite || notesEmpty) ? (auto.data.factory_notes || "") : f.factory_notes,
        material: (hint && materialIsDefault) ? hint : f.material,
      }));
      if (auto.data.factory_notes) {
        toast.success("Factory notes refreshed from your brief");
      }
    } catch (autoErr) {
      console.warn("Auto-complete failed", autoErr);
      toast.error(autoErr?.response?.data?.detail || "Auto-complete failed");
    }
  };

  // A) Generate a NEW product image AND new factory notes
  const generateImage = async () => {
    const prompt = genPrompt || `${form.color} ${form.material} ${form.clothing_type}, ${form.description}`;
    if (!prompt.trim()) {
      toast.error("Add a description first");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post("/ai/generate-image", { prompt, style: "clothing" });
      upd("image_base64", `data:${r.data.mime_type};base64,${r.data.image_base64}`);
      // The backend now auto-generates the matching back view too — take it if present
      if (r.data.image_back_base64) {
        upd("image_back", `data:${r.data.mime_type};base64,${r.data.image_back_base64}`);
        toast.success("Front + back images generated — auto-completing the factory spec…");
      } else {
        toast.success("Image generated — now auto-completing the factory spec…");
      }
      await runAutoComplete(prompt, { forceOverwrite: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  // B) KEEP the current image, just re-run factory notes from the latest prompt
  const regenerateNotesOnly = async () => {
    const prompt = genPrompt || form.description || `${form.color} ${form.material} ${form.clothing_type}`;
    if (!prompt.trim()) {
      toast.error("Nothing to base notes on — write a description first");
      return;
    }
    if (!form.image_base64) {
      toast.info("No saved image yet — generating a new one");
      return generateImage();
    }
    setGenerating(true);
    try {
      await runAutoComplete(prompt, { forceOverwrite: true });
    } finally {
      setGenerating(false);
    }
  };

  // C) Generate Back + Side views from the same prompt (for 360° spin viewer & PDF tech-pack)
  const generateViews = async () => {
    const prompt = genPrompt || form.ai_prompt || form.description || `${form.color} ${form.material} ${form.clothing_type}`;
    if (!prompt.trim()) {
      toast.error("Add a description first");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post("/ai/generate-views", {
        prompt,
        which: ["back", "side"],
      });
      const views = r.data?.views || {};
      if (views.back) {
        upd("image_back", `data:${views.back.mime_type};base64,${views.back.image_base64}`);
      }
      if (views.side) {
        upd("image_side", `data:${views.side.mime_type};base64,${views.side.image_base64}`);
      }
      const got = [views.back && "BACK", views.side && "SIDE"].filter(Boolean).join(" + ");
      if (got) {
        toast.success(`${got} view${got.includes("+") ? "s" : ""} generated — 360° spin ready`);
      } else {
        toast.error("No views generated");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "View generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const suggestDims = async () => {
    setSuggestingDims(true);
    try {
      const r = await api.post("/ai/suggest-dimensions", {
        clothing_type: form.clothing_type,
        description: form.description,
        unit: "cm",
      });
      upd("dimensions", r.data);
      toast.success("Dimensions suggested");
    } catch (e) {
      toast.error("AI dimension suggestion failed");
    } finally {
      setSuggestingDims(false);
    }
  };

  const loadStandardPreset = async () => {
    setLoadingPreset(true);
    try {
      const r = await api.get(`/presets/dimensions/${encodeURIComponent(form.clothing_type)}`);
      upd("dimensions", r.data.dimensions);
      toast.success(`Loaded standard ${form.clothing_type} measurements (cm)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No preset available for this type");
    } finally {
      setLoadingPreset(false);
    }
  };

  const loadPanelPreset = async () => {
    try {
      const r = await api.get(`/presets/panels/${encodeURIComponent(form.clothing_type)}`);
      const panels = (r.data.panels || []).map((p) => ({ panel: p, material: "" }));
      upd("materials", panels);
      toast.success(`${panels.length} panels loaded`);
    } catch (e) {
      toast.error("Failed to load panel preset");
    }
  };

  const addMaterialRow = () => {
    upd("materials", [...(form.materials || []), { panel: "", material: "" }]);
  };

  const updateMaterialRow = (idx, field, val) => {
    const next = [...(form.materials || [])];
    next[idx] = { ...next[idx], [field]: val };
    upd("materials", next);
  };

  const removeMaterialRow = (idx) => {
    const next = [...(form.materials || [])];
    next.splice(idx, 1);
    upd("materials", next);
  };

  const genPalette = async () => {
    const mood = paletteMood.trim() || `${form.clothing_type} streetwear`;
    setPalLoading(true);
    try {
      const r = await api.post("/ai/color-palette", { mood, count: 5 });
      setPalettes(r.data.palettes || []);
    } catch (e) {
      toast.error("Palette generation failed");
    } finally {
      setPalLoading(false);
    }
  };

  const aiFactoryNotes = async () => {
    setGenNotes(true);
    try {
      const r = await api.post("/ai/factory-notes", {
        clothing_type: form.clothing_type,
        garment_style: form.garment_style,
        color: form.color,
        material: form.material,
        description: form.description || genPrompt,
        dimensions: form.dimensions,
        materials: form.materials,
        units_per_size: parseInt(form.units_per_size) || 1,
        logo_placement: form.logo_overlay?.placement || "",
        reference_image_url: form.reference_image || "",
      });
      upd("factory_notes", r.data.notes);
      toast.success("Factory notes generated");
    } catch (e) {
      toast.error("Factory notes generation failed");
    } finally {
      setGenNotes(false);
    }
  };

  const save = async (publishFlag = null) => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const published = publishFlag === null ? !!form.published : publishFlag;
      const payload = {
        ...form,
        published,
        price: parseFloat(form.price) || 0,
        stock: parseInt(form.stock) || 0,
        units_per_size: Math.max(parseInt(form.units_per_size) || 1, 1),
        cost_to_make: parseFloat(form.cost_to_make) || 0,
        ai_prompt: genPrompt || form.ai_prompt || "",
      };
      let res;
      if (isEdit) res = await api.put(`/admin/products/${id}`, payload);
      else res = await api.post("/admin/products", payload);
      if (publishFlag !== null) upd("published", published);
      toast.success(published ? "Saved & published to shop" : "Saved as draft");
      // Clear local auto-save draft once we've saved to the server
      localStorage.removeItem(DRAFT_KEY);
      if (!isEdit) nav(`/admin/design/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const exportPDF = () => {
    if (!isEdit) return toast.error("Save first");
    window.open(`${API}/products/${id}/export/pdf`, "_blank");
  };

  const exportJSON = () => {
    if (!isEdit) return toast.error("Save first");
    window.open(`${API}/products/${id}/export/json`, "_blank");
  };

  const imgSrc = form.image_base64
    ? (form.image_base64.startsWith("data:") ? form.image_base64 : `data:image/png;base64,${form.image_base64}`)
    : null;

  return (
    <div className="min-h-screen text-white">
      <RawHeader variant="admin" />
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin" className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white mb-2 inline-block">
              ← BACK
            </Link>
            <h1 className="font-display text-3xl lg:text-4xl">
              {isEdit ? "EDIT DESIGN" : "NEW DESIGN"}
            </h1>
            {!isEdit && (
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 mt-1 flex items-center gap-2" data-testid="autosave-status">
                <span className="w-1.5 h-1.5 bg-[#16a34a] rounded-full inline-block" />
                Auto-saving as you type · safe to come back later
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="help-btn"
              onClick={() => setHelpOpen(true)}
              className="btn-raw text-[10px]"
            >
              ? HOW TO USE
            </button>
            {isEdit && (
              <>
                <button data-testid="export-json" onClick={exportJSON} className="btn-raw">JSON ↓</button>
                <button data-testid="export-pdf" onClick={exportPDF} className="btn-raw">PDF ↓</button>
              </>
            )}
            <button
              data-testid="save-design"
              onClick={() => save(true)}
              disabled={saving}
              className="btn-raw btn-raw-primary"
              title="Always publishes to the shop floor — no drafts."
            >
              {saving ? "SAVING..." : "▶ SAVE TO SHOP FLOOR"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-px bg-zinc-800 border border-zinc-800">
          {/* LEFT: Visual + basic info */}
          <div className="bg-[#050505] p-5 flex flex-col gap-5">
            {/* Visual */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-raw">Visual Output</label>
                <div className="flex gap-1">
                  <button onClick={() => setViewMode("img")} className={`px-3 py-1 text-[9px] tracking-[0.2em] uppercase border ${viewMode === "img" ? "bg-white text-black border-white" : "border-zinc-800 text-zinc-500"}`}>Photo</button>
                  <button onClick={() => setViewMode("3d")} className={`px-3 py-1 text-[9px] tracking-[0.2em] uppercase border ${viewMode === "3d" ? "bg-white text-black border-white" : "border-zinc-800 text-zinc-500"}`}>3D</button>
                </div>
              </div>
              <div className="aspect-square">
                {viewMode === "3d" ? (
                  <ViewerErrorBoundary>
                    <ClothingViewer3D
                      type={form.clothing_type}
                      color={form.color || "#d4af37"}
                      logoOverlay={form.logo_overlay}
                      imageSrc={imgSrc}
                      autoRotate
                    />
                  </ViewerErrorBoundary>
                ) : (
                  <div className="w-full h-full bg-[#0a0a0a] border border-zinc-800 grid-bg relative">
                    {imgSrc ? (
                      <img src={imgSrc} alt="" className="absolute inset-0 w-full h-full object-contain" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-zinc-600 text-xs tracking-[0.3em] uppercase">
                        // NO IMAGE GENERATED
                      </div>
                    )}
                    <div className="absolute top-3 left-3 text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-mono">
                      // GEMINI_NANO_BANANA.OUT
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* AI image generator */}
            <div className="border border-zinc-800 bg-[#0a0a0a] p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37]">// AI_IMAGE_GENERATOR</div>
                <button
                  type="button"
                  data-testid="insert-keyword-template"
                  onClick={() =>
                    setGenPrompt(
                      "Garment type: \n" +
                      "Colour: \n" +
                      "Pattern / artwork: \n" +
                      "Fit / cut: (slim / regular / oversized / baggy) \n" +
                      "Material / fabric: (e.g. merino wool, silk, heavyweight french terry cotton, denim)\n" +
                      "Details: (pockets, drawcords, zip, cuffs, collar style) \n" +
                      "Logo placement: (e.g. chest-left, centre-chest, back, sleeve, hem) \n" +
                      "Pattern placement: (e.g. across sleeves, full body, yoke only) \n" +
                      "Vibe / style reference: "
                    )
                  }
                  className="text-[9px] tracking-[0.2em] uppercase text-[#d4af37] hover:underline"
                >
                  + INSERT KEYWORD TEMPLATE
                </button>
              </div>
              <textarea
                data-testid="ai-prompt"
                value={genPrompt}
                onChange={(e) => setGenPrompt(e.target.value)}
                placeholder={
                  "Tip: click '+ INSERT KEYWORD TEMPLATE' to fill the fields below, or type freely — e.g.\n" +
                  "Colour: black with green camo on sleeves\n" +
                  "Fit: oversized / baggy\n" +
                  "Material: merino wool\n" +
                  "Logo placement: chest-left, embroidered RC gold"
                }
                rows={8}
                className="input-raw w-full mb-2 resize-none font-mono text-xs leading-relaxed"
              />
              <button
                data-testid="generate-image-btn"
                onClick={generateImage}
                disabled={generating}
                className="btn-raw btn-raw-primary w-full text-[10px]"
              >
                {generating ? "GENERATING..." : "→ GENERATE NEW IMAGE + FACTORY NOTES"}
              </button>
              {isEdit && form.image_base64 && (
                <button
                  data-testid="regenerate-notes-btn"
                  onClick={regenerateNotesOnly}
                  disabled={generating}
                  className="btn-raw w-full text-[10px] mt-2"
                >
                  {generating ? "REFRESHING..." : "↻ REGENERATE FACTORY NOTES (KEEP THIS IMAGE)"}
                </button>
              )}
              {form.image_base64 && (
                <button
                  data-testid="generate-views-btn"
                  onClick={generateViews}
                  disabled={generating}
                  className="btn-raw w-full text-[10px] mt-2"
                  title="AI-generates BACK and SIDE views from the same prompt so shoppers get a 360° spin and the factory PDF shows all three angles"
                >
                  {generating ? "GENERATING VIEWS..." : "⟳ GENERATE BACK + SIDE (360° VIEWS)"}
                </button>
              )}
              <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 mt-2 leading-relaxed">
                // KEYWORDS WE DETECT FOR FACTORY NOTES: FABRIC (wool, silk, cashmere, linen, denim, fleece, corduroy…), PATTERNS (camo, stripe, plaid, tartan, houndstooth, paisley, floral, leopard, polka…), COLOURS, FIT (slim / oversized / baggy), LOGO PLACEMENT.
              </div>
            </div>

            {/* Upload */}
            <div className="space-y-3">
              <div>
                <label className="label-raw">Front Image (or upload)</label>
                <input
                  data-testid="upload-image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const r = new FileReader();
                    r.onload = () => upd("image_base64", r.result);
                    r.readAsDataURL(f);
                  }}
                  className="input-raw text-xs file:mr-3 file:px-3 file:py-1 file:border-0 file:bg-zinc-900 file:text-white file:text-xs file:uppercase file:tracking-wider"
                />
              </div>
              <div>
                <label className="label-raw">Back Image (for 360°)</label>
                <input
                  data-testid="upload-image-back"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const r = new FileReader();
                    r.onload = () => upd("image_back", r.result);
                    r.readAsDataURL(f);
                  }}
                  className="input-raw text-xs file:mr-3 file:px-3 file:py-1 file:border-0 file:bg-zinc-900 file:text-white file:text-xs file:uppercase file:tracking-wider"
                />
                {form.image_back && (
                  <button
                    type="button"
                    onClick={() => upd("image_back", "")}
                    className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 hover:text-[#d4af37] mt-1"
                  >
                    ✕ REMOVE BACK IMAGE
                  </button>
                )}
              </div>
              <div>
                <label className="label-raw">Side Image (optional)</label>
                <input
                  data-testid="upload-image-side"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const r = new FileReader();
                    r.onload = () => upd("image_side", r.result);
                    r.readAsDataURL(f);
                  }}
                  className="input-raw text-xs file:mr-3 file:px-3 file:py-1 file:border-0 file:bg-zinc-900 file:text-white file:text-xs file:uppercase file:tracking-wider"
                />
                {form.image_side && (
                  <button
                    type="button"
                    onClick={() => upd("image_side", "")}
                    className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 hover:text-[#d4af37] mt-1"
                  >
                    ✕ REMOVE SIDE IMAGE
                  </button>
                )}
              </div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 border-l-2 border-[#d4af37] pl-2">
                // Upload back & side photos for a real rotating 360° view in the shop.
              </div>
              <div>
                <label className="label-raw">Factory Reference Image (optional)</label>
                <input
                  data-testid="upload-reference-image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const r = new FileReader();
                    r.onload = () => upd("reference_image", r.result);
                    r.readAsDataURL(f);
                  }}
                  className="input-raw text-xs file:mr-3 file:px-3 file:py-1 file:border-0 file:bg-zinc-900 file:text-white file:text-xs file:uppercase file:tracking-wider"
                />
                {form.reference_image && (
                  <div className="mt-2 flex items-center gap-3">
                    <img src={form.reference_image} alt="reference" className="w-16 h-16 object-cover border border-[#d4af37]/50" />
                    <button
                      type="button"
                      onClick={() => upd("reference_image", "")}
                      className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 hover:text-[#d4af37]"
                    >
                      ✕ REMOVE REFERENCE
                    </button>
                  </div>
                )}
                <div className="text-[9px] tracking-[0.2em] uppercase text-[#d4af37]/70 mt-1">
                  // Attached to factory-notes &amp; tech-pack so the factory sees your visual reference.
                </div>
              </div>

              {/* Multi-image Gallery — extra detail shots shown on product page */}
              <div>
                <label className="label-raw">Gallery / Detail Shots (optional, multiple)</label>
                <input
                  data-testid="upload-gallery-images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (!files.length) return;
                    Promise.all(
                      files.map(
                        (f) =>
                          new Promise((resolve) => {
                            const r = new FileReader();
                            r.onload = () => resolve(r.result);
                            r.readAsDataURL(f);
                          })
                      )
                    ).then((urls) => {
                      upd("gallery_images", [...(form.gallery_images || []), ...urls].slice(0, 12));
                    });
                    e.target.value = "";
                  }}
                  className="input-raw text-xs file:mr-3 file:px-3 file:py-1 file:border-0 file:bg-zinc-900 file:text-white file:text-xs file:uppercase file:tracking-wider"
                />
                {Array.isArray(form.gallery_images) && form.gallery_images.length > 0 && (
                  <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {form.gallery_images.map((g, i) => (
                      <div key={i} className="relative group">
                        <img src={g} alt={`gallery ${i + 1}`} className="w-full aspect-square object-cover border border-[#d4af37]/40" />
                        <button
                          type="button"
                          data-testid={`gallery-remove-${i}`}
                          onClick={() => upd("gallery_images", form.gallery_images.filter((_, idx) => idx !== i))}
                          className="absolute top-1 right-1 bg-black/80 border border-[#d4af37] text-[#d4af37] text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-[9px] tracking-[0.2em] uppercase text-[#d4af37]/70 mt-1">
                  // Up to 12 extra images (stitching close-ups, fabric texture, on-model shots). Shown under the 3D viewer on the product page.
                </div>
              </div>
            </div>

            {/* Logo Overlay on Garment */}
            <div className="border border-zinc-800 bg-[#0a0a0a] p-4">
              <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// LOGO_ON_GARMENT</div>
              <div className="space-y-3">
                <div>
                  <label className="label-raw">Pick From Your Saved Logos</label>
                  {savedLogos.length === 0 ? (
                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 border border-dashed border-zinc-800 p-3">
                      No logos yet. Create one in{" "}
                      <Link to="/admin/logo" className="text-[#d4af37] hover:underline">LOGO STUDIO</Link>.
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-1">
                      <button
                        data-testid="logo-pick-none"
                        type="button"
                        onClick={() => upd("logo_overlay", { ...form.logo_overlay, logo_id: "", image_base64: "", placement: "none" })}
                        className={`aspect-square border text-[9px] uppercase tracking-[0.15em] ${!form.logo_overlay?.image_base64 ? "border-[#d4af37] text-[#d4af37]" : "border-zinc-800 text-zinc-500 hover:border-white hover:text-white"}`}
                      >
                        NONE
                      </button>
                      {savedLogos.map((l) => (
                        <button
                          key={l.id}
                          data-testid={`logo-pick-${l.id}`}
                          type="button"
                          onClick={() => upd("logo_overlay", {
                            ...(form.logo_overlay || {}),
                            logo_id: l.id,
                            image_base64: l.image_base64,
                            placement: form.logo_overlay?.placement === "none" ? "front-chest-left" : (form.logo_overlay?.placement || "front-chest-left"),
                            size: form.logo_overlay?.size ?? 1,
                          })}
                          className={`aspect-square border overflow-hidden bg-[#050505] grid-bg relative ${form.logo_overlay?.logo_id === l.id ? "border-[#d4af37]" : "border-zinc-800 hover:border-white"}`}
                          title={l.name}
                        >
                          <img src={l.image_base64} alt={l.name} className="absolute inset-0 w-full h-full object-contain" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="label-raw">Placement</label>
                  <select
                    data-testid="field-logo-placement"
                    className="input-raw"
                    value={form.logo_overlay?.placement || "none"}
                    onChange={(e) => upd("logo_overlay", { ...(form.logo_overlay || {}), placement: e.target.value })}
                  >
                    <option value="none">— No Logo —</option>
                    <option value="front-chest-left">Front · Chest Left</option>
                    <option value="front-chest-center">Front · Chest Center</option>
                    <option value="front-chest-right">Front · Chest Right</option>
                    <option value="front-center-large">Front · Center Large</option>
                    <option value="back-top-center">Back · Top Center (Yoke)</option>
                    <option value="back-center-large">Back · Center Large Print</option>
                    <option value="sleeve-left">Left Sleeve</option>
                    <option value="sleeve-right">Right Sleeve</option>
                  </select>
                </div>

                <div>
                  <label className="label-raw">Logo Size: {((form.logo_overlay?.size ?? 1) * 100).toFixed(0)}%</label>
                  <input
                    data-testid="field-logo-size"
                    type="range"
                    min="0.3"
                    max="1.5"
                    step="0.05"
                    value={form.logo_overlay?.size ?? 1}
                    onChange={(e) => upd("logo_overlay", { ...(form.logo_overlay || {}), size: parseFloat(e.target.value) })}
                    className="w-full accent-[#d4af37]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: form */}
          <div className="bg-[#050505] p-5 space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="label-raw">Name *</label>
                <input data-testid="field-name" className="input-raw" value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="RAW // HOODIE 001" />
              </div>
              <div>
                <label className="label-raw">Type</label>
                <select data-testid="field-type" className="input-raw" value={form.clothing_type} onChange={(e) => { upd("clothing_type", e.target.value); upd("garment_style", ""); }}>
                  {CLOTHING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="label-raw">Style / Cut</label>
                <select
                  data-testid="field-garment-style"
                  className="input-raw"
                  value={form.garment_style}
                  onChange={(e) => upd("garment_style", e.target.value)}
                >
                  <option value="">— Select Style —</option>
                  {garmentStyles.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="label-raw">Gender / Section</label>
                <select
                  data-testid="field-gender"
                  className="input-raw"
                  value={form.gender}
                  onChange={(e) => upd("gender", e.target.value)}
                >
                  <option value="unisex">UNISEX — Appears in Men + Women</option>
                  <option value="men">MEN — Men's section only</option>
                  <option value="women">WOMEN — Women's section only</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="label-raw">Base Garment Colour</label>
                <div className="flex gap-2">
                  <input type="color" value={form.color} onChange={(e) => upd("color", e.target.value)} className="w-14 h-[42px] bg-transparent border border-zinc-800 cursor-pointer" />
                  <input data-testid="field-color" className="input-raw flex-1" value={form.color} onChange={(e) => upd("color", e.target.value)} placeholder="#000000 or name" />
                  <button
                    data-testid="ai-palette-btn"
                    type="button"
                    onClick={() => setPaletteOpen(true)}
                    className="btn-raw text-[10px] whitespace-nowrap px-3"
                  >
                    ⚡ AI PALETTE
                  </button>
                </div>
                {/* Color swatches */}
                <div className="flex gap-1 mt-2 flex-wrap">
                  {["#000000", "#ffffff", "#6b7280", "#1e3a8a", "#dc2626", "#166534", "#a16207", "#7c2d12", "#be185d", "#4c1d95"].map((c) => (
                    <button
                      key={c}
                      data-testid={`swatch-${c}`}
                      type="button"
                      onClick={() => upd("color", c)}
                      className="w-7 h-7 border border-zinc-700 hover:border-white transition-colors"
                      style={{ background: c }}
                      title={c}
                    />
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <label className="label-raw">Description</label>
                <textarea data-testid="field-description" rows={3} className="input-raw resize-none" value={form.description} onChange={(e) => upd("description", e.target.value)} placeholder="Short product description" />
              </div>
              <div className="col-span-2">
                <label className="label-raw">Material / Fabric</label>
                <input data-testid="field-material" className="input-raw" value={form.material} onChange={(e) => upd("material", e.target.value)} />
              </div>
              <div>
                <label className="label-raw">Price (GBP £)</label>
                <input data-testid="field-price" type="number" step="0.01" className="input-raw" value={form.price} onChange={(e) => upd("price", e.target.value)} />
              </div>
              <div>
                <label className="label-raw">Stock</label>
                <input data-testid="field-stock" type="number" className="input-raw" value={form.stock} onChange={(e) => upd("stock", e.target.value)} />
              </div>
              <div>
                <label className="label-raw">Cost To Make (£ / unit)</label>
                <input data-testid="field-cost" type="number" step="0.01" className="input-raw" value={form.cost_to_make} onChange={(e) => upd("cost_to_make", e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className="label-raw">Units Per Size (S/M/L/XL)</label>
                <input
                  data-testid="field-units-per-size"
                  type="number"
                  min="1"
                  step="1"
                  className="input-raw"
                  value={form.units_per_size}
                  onChange={(e) => upd("units_per_size", e.target.value)}
                  placeholder="1"
                />
                <div className="text-[9px] tracking-[0.2em] uppercase text-[#d4af37]/70 mt-1">
                  {`FACTORY MAKES ${Math.max(parseInt(form.units_per_size) || 1, 1) * 4} TOTAL (${Math.max(parseInt(form.units_per_size) || 1, 1)}× S/M/L/XL)`}
                </div>
              </div>
              <div className="col-span-2 flex items-center gap-3 border border-[#16a34a]/40 bg-[#16a34a]/5 p-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#16a34a]" />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#16a34a]">GOES LIVE ON SAVE — NO DRAFTS</span>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">DROP</span>
                  <input
                    data-testid="field-drop-number"
                    type="number"
                    min="1"
                    step="1"
                    value={form.drop_number || 1}
                    onChange={(e) => upd("drop_number", Math.max(parseInt(e.target.value, 10) || 1, 1))}
                    className="w-16 bg-black border border-zinc-700 text-[#d4af37] px-2 py-1 text-center font-mono text-sm focus:border-[#d4af37] outline-none"
                    title="Assign this design to a collection drop (Drop 01, Drop 02, ...)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dimensions */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">// FACTORY_SPECS</div>
              <h2 className="font-display text-2xl">DIMENSIONS (cm)</h2>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="load-preset-dims" onClick={loadStandardPreset} disabled={loadingPreset} className="btn-raw text-[10px]">
                {loadingPreset ? "LOADING..." : `⟱ APPLY ${form.clothing_type.toUpperCase()} PRESET`}
              </button>
              <button data-testid="ai-suggest-dims" onClick={suggestDims} disabled={suggestingDims} className="btn-raw text-[10px]">
                {suggestingDims ? "THINKING..." : "⚡ AI REFINE"}
              </button>
            </div>
          </div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-3">
            // Click "APPLY PRESET" to load standard industry measurements for {form.clothing_type}, then fine-tune with AI or manually.
          </p>
          <DimensionTable dimensions={form.dimensions} onChange={(d) => upd("dimensions", d)} />
        </div>

        {/* Material Panels */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">// FABRIC_MAP</div>
              <h2 className="font-display text-2xl">MATERIAL COMPOSITION</h2>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="load-panel-preset" onClick={loadPanelPreset} className="btn-raw text-[10px]">
                ⟱ LOAD PANELS
              </button>
              <button data-testid="add-material-row" onClick={addMaterialRow} className="btn-raw text-[10px]">+ ADD ROW</button>
            </div>
          </div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-3">
            // Assign a fabric to each garment panel. Factory uses this to source materials.
          </p>
          <div className="border border-zinc-800" data-testid="materials-table">
            <div className="grid grid-cols-[200px_1fr_60px] text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-bold border-b border-zinc-800 bg-[#0a0a0a]">
              <div className="p-3 border-r border-zinc-800">PANEL</div>
              <div className="p-3 border-r border-zinc-800">MATERIAL / FABRIC</div>
              <div className="p-3 text-center">—</div>
            </div>
            {(form.materials || []).length === 0 ? (
              <div className="p-8 text-center text-zinc-600 text-xs tracking-[0.2em] uppercase">
                NO PANELS — CLICK "LOAD PANELS" TO AUTO-FILL FROM {form.clothing_type.toUpperCase()} TEMPLATE
              </div>
            ) : (
              form.materials.map((m, idx) => (
                <div key={idx} className="grid grid-cols-[200px_1fr_60px] border-b border-zinc-800 last:border-b-0">
                  <div className="border-r border-zinc-800">
                    <input
                      data-testid={`mat-panel-${idx}`}
                      className="w-full h-full bg-transparent text-white font-mono text-sm px-3 py-3 outline-none focus:bg-[#d4af37]/10"
                      value={m.panel}
                      onChange={(e) => updateMaterialRow(idx, "panel", e.target.value)}
                      placeholder="e.g. Body"
                    />
                  </div>
                  <div className="border-r border-zinc-800 relative">
                    <input
                      data-testid={`mat-fabric-${idx}`}
                      list={`materials-list-${idx}`}
                      className="w-full h-full bg-transparent text-white font-mono text-sm px-3 py-3 outline-none focus:bg-[#d4af37]/10"
                      value={m.material}
                      onChange={(e) => updateMaterialRow(idx, "material", e.target.value)}
                      placeholder="Select or type a fabric..."
                    />
                    <datalist id={`materials-list-${idx}`}>
                      {materialLibrary.map((lib, i) => (
                        <option key={i} value={lib.name}>{lib.use}</option>
                      ))}
                    </datalist>
                  </div>
                  <button
                    data-testid={`mat-del-${idx}`}
                    onClick={() => removeMaterialRow(idx)}
                    className="text-zinc-600 hover:text-[#d4af37] text-xs tracking-[0.2em] uppercase"
                  >
                    DEL
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Factory notes */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">// FACTORY_NOTES</div>
              <h2 className="font-display text-2xl">CONSTRUCTION NOTES</h2>
            </div>
            <button data-testid="ai-factory-notes" onClick={aiFactoryNotes} disabled={genNotes} className="btn-raw text-[10px]">
              {genNotes ? "WRITING..." : "⚡ AI GENERATE"}
            </button>
          </div>
          <textarea
            data-testid="field-factory-notes"
            rows={10}
            value={form.factory_notes}
            onChange={(e) => upd("factory_notes", e.target.value)}
            placeholder="Construction, stitching, fabric, trims, packaging, tolerances..."
            className="input-raw font-mono text-sm leading-relaxed resize-y"
          />
        </div>

        <div className="h-20" />
      </div>
      <HelpDiagram open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* AI Colour Palette Modal */}
      {paletteOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm overflow-y-auto"
          data-testid="palette-modal"
          onClick={() => setPaletteOpen(false)}
        >
          <div className="min-h-full py-10 px-4 flex items-start justify-center">
            <div
              className="w-full max-w-3xl border border-zinc-800 bg-[#050505] p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-1">// AI_COLOUR_PALETTE</div>
                  <h2 className="font-display text-2xl">PICK A PALETTE</h2>
                </div>
                <button
                  onClick={() => setPaletteOpen(false)}
                  className="w-10 h-10 border border-zinc-700 text-zinc-400 hover:border-[#d4af37] hover:text-[#d4af37] flex items-center justify-center text-xl"
                >
                  ×
                </button>
              </div>
              <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-4">
                Describe a mood or theme — the AI will generate fashion-ready palettes.
              </p>
              <div className="flex gap-2 mb-5">
                <input
                  data-testid="palette-mood"
                  value={paletteMood}
                  onChange={(e) => setPaletteMood(e.target.value)}
                  placeholder="e.g. moody industrial streetwear / summer coastal / retro 90s"
                  className="input-raw flex-1"
                />
                <button
                  data-testid="palette-generate"
                  onClick={genPalette}
                  disabled={palLoading}
                  className="btn-raw btn-raw-primary"
                >
                  {palLoading ? "GENERATING..." : "⚡ GENERATE"}
                </button>
              </div>

              {palettes.length === 0 && !palLoading && (
                <div className="border border-dashed border-zinc-800 p-8 text-center text-zinc-600 text-xs tracking-[0.2em] uppercase">
                  Type a mood and click GENERATE
                </div>
              )}

              <div className="space-y-3">
                {palettes.map((pal, i) => (
                  <div key={i} className="border border-zinc-800 bg-[#0a0a0a] p-4" data-testid={`palette-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-display text-lg">{pal.name}</div>
                      <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-600 font-mono">
                        0{i + 1}
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-1">
                      {(pal.colors || []).map((c, j) => (
                        <button
                          key={j}
                          onClick={() => {
                            upd("color", c);
                            toast.success(`Colour set to ${c}`);
                            setPaletteOpen(false);
                          }}
                          className="aspect-square border border-zinc-800 hover:border-white transition-colors group relative"
                          style={{ background: c }}
                          data-testid={`palette-${i}-color-${j}`}
                        >
                          <span className="absolute bottom-1 left-1 right-1 font-mono text-[8px] tracking-wider bg-black/70 text-white px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {c}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDesignStudio;
